import React from 'react';
import { Button } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';

import { BlockedByFeature, notAvailablePropExceptIn } from '../../../../featureToggles/features/utils';
import { selectors as accountListSelectors } from '../../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../../slices/auth';
import { RootState } from '../../../../slices/rootReducer';
import { DeleteModalOld } from '../../../common/deleteModal';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { startCancellationProcessForPlgExternal2 } from '../../../../slices/customer/customer.thunk';
import { selectors as customerSelectors } from '../../../../slices/customer/customer';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { RemovePaidSeats } from '../../../common/removeSeats/RemovePaidSeats';
import { FeatureToggle, Off, On } from '../../../../featureToggles';

import styles from './editSubscription.module.scss';

interface IMapStateProps {
  renewalDate?: Date;
  accessType?: AccessTypes;
  cancelling: boolean | typeof BlockedByFeature;
  accountId?: string | typeof BlockedByFeature;
}

interface IMapDispatchProps {
  startCancellationProcess: (accountId: string) => void;
}

interface EditSubscriptionState {
  confirmationModalOpen: boolean;
  // props for plgexternal2
  removePaidSeatModalOpen: boolean;
}

type Props = IMapStateProps & IMapDispatchProps;

class EditSubscriptionBase extends React.Component<Props, EditSubscriptionState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      confirmationModalOpen: false,
      removePaidSeatModalOpen: false
    };
    this.toggleCancelModalOpen = this.toggleCancelModalOpen.bind(this);
    this.toggleRemovePaidSeatModalOpen = this.toggleRemovePaidSeatModalOpen.bind(this);
    this.startCancellationProcess = this.startCancellationProcess.bind(this);
  }

  private toggleCancelModalOpen() {
    this.setState(state => ({ confirmationModalOpen: !state.confirmationModalOpen }));
  }

  private toggleRemovePaidSeatModalOpen() {
    this.setState(state => ({ removePaidSeatModalOpen: !state.removePaidSeatModalOpen }));
  }

  private startCancellationProcess() {
    const { accountId } = this.props;
    if (accountId === BlockedByFeature || !accountId) {
      return;
    }
    this.props.startCancellationProcess(accountId);
    this.setState({
      confirmationModalOpen: false
    });
  }

  private renderCancelationModal() {
    const { renewalDate } = this.props;
    const { confirmationModalOpen } = this.state;
    if (!confirmationModalOpen) {
      return null;
    }
    return <DeleteModalOld
      title='Cancel subscription'
      description={`Are you sure you want to cancel your SightX subscription? You will lose access to the platform on ${moment(renewalDate).format('MMMM DD, YYYY')}.`}
      onCancel={this.toggleCancelModalOpen}
      onProceed={this.startCancellationProcess}
    />;
  }

  private renderRemoveSeatsModal() {
    const { accountId } = this.props;
    const { removePaidSeatModalOpen } = this.state;
    if (!isFeatureEnabled(Features.PlgExternal2) || !accountId) {
      return null;
    }
    return <RemovePaidSeats
      visible={removePaidSeatModalOpen}
      accountId={accountId}
      onCancel={this.toggleRemovePaidSeatModalOpen}
    />;
  }

  render() {
    const { renewalDate, accessType, cancelling } = this.props;
    const isRequestingToCancel = cancelling === BlockedByFeature ? false : cancelling;
    if (accessType !== AccessTypes.Professional) {
      return <Redirect to="/settings/subscription" />;
    }
    return (
      <div className={styles.card}>
        <div className={styles.title}>Edit SightX subscription</div>
        <div className={styles.description}>All edits to your subscription will take effect at the beginning of your next billing cycle on <b>{moment(renewalDate).format('MMMM DD, YYYY')}</b>.</div>
        <div className={styles.actions}>
          <FeatureToggle feature={Features.PlgExternal2}>
            <On>
              <Button type="primary" onClick={this.toggleRemovePaidSeatModalOpen} loading={isRequestingToCancel} disabled={isRequestingToCancel}>
                Remove paid seats
              </Button>
            </On>
            <Off />
          </FeatureToggle>
          <Button type="link" onClick={this.toggleCancelModalOpen} loading={isRequestingToCancel} disabled={isRequestingToCancel}>
            Cancel subscription
          </Button>
        </div>
        {this.renderCancelationModal()}
        {this.renderRemoveSeatsModal()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateProps => {
  const user = authSelectors.getLoggedInUser(state);
  const account = accountListSelectors.getAccountById(state, user?.account.id || '');
  const renewalDate = account && account.subscription ? new Date(account.subscription.cycleEndDate * 1000) : undefined;
  return {
    renewalDate,
    accessType: authSelectors.getAuthAccountAccessType(state),
    cancelling: customerSelectors.isCancelling(state),
    accountId: notAvailablePropExceptIn({ expected: user?.account.id, availableIn: [Features.PlgExternal2] })
  };
};

export const EditSubscription = connect(mapStateToProps, {
  startCancellationProcess: startCancellationProcessForPlgExternal2
})(EditSubscriptionBase);
